import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mealsApi } from '@/shared/api/endpoints';
import type { Meal } from '@/shared/api/types';
import { describeDay, formatTime, shiftIsoDate, todayIso } from '@/shared/format/dates';
import { formatKcal, mealTypeLabel } from '@/shared/format/nutrition';
import { describeError, useAsync } from '@/shared/hooks/useAsync';
import { Banner, Button, Card, EmptyState, Eyebrow, Field } from '@/shared/ui';
import { HistoryIcon } from '@/shared/ui/icons';
import { ItemThumb } from '@/shared/ui/MediaThumb';
import { EsqueletoLista, useEsqueleto } from '@/shared/ui/Skeleton';

/** Los días que se traen de una vez. Alcanza para reconocer una semana sin pedir catorce veces. */
const DAYS = 7;

function lastDays(count: number): string[] {
  const today = todayIso();
  return Array.from({ length: count }, (_, index) => shiftIsoDate(today, -index));
}

/**
 * S-10 · el historial.
 *
 * Existe sobre todo para duplicar: quien come parecido todos los días no debería volver a
 * fotografiar y declarar lo mismo. Duplicar copia los ítems y las estimaciones vigentes **sin**
 * volver a resolver (RF-15), así que es instantáneo y no gasta cupo de visión.
 */
export function HistoryPage() {
  const navigate = useNavigate();
  const days = useMemo(() => lastDays(DAYS), []);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState<string | null>(null);

  const history = useAsync<Array<{ date: string; meals: Meal[] }>>(
    async () =>
      Promise.all(days.map(async (date) => ({ date, meals: await mealsApi.forDay(date) }))),
    [days],
  );

  const duplicate = async (mealId: string) => {
    setDuplicating(mealId);
    setError(null);
    try {
      const copy = await mealsApi.duplicate(mealId);
      navigate(`/comidas/${copy.id}`);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setDuplicating(null);
    }
  };

  const needle = query.trim().toLowerCase();
  const visible = (history.data ?? [])
    .map((day) => ({
      date: day.date,
      meals:
        needle === ''
          ? day.meals
          : day.meals.filter((meal) =>
              meal.items.some((item) => (item.freeText ?? '').toLowerCase().includes(needle)),
            ),
    }))
    .filter((day) => day.meals.length > 0);

  const esqueleto = useEsqueleto(history.loading);

  return (
    <>
      <h1>Historial</h1>

      <Field
        label="Buscar"
        placeholder="Milanesa, yogur, café…"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {error !== null && <Banner tone="danger">{error}</Banner>}
      {esqueleto && history.data === null && (
        <EsqueletoLista etiqueta="Cargando el historial" filas={5} />
      )}
      {history.error !== null && <Banner tone="danger">{history.error}</Banner>}

      {history.data !== null && visible.length === 0 && (
        <EmptyState
          icon={<HistoryIcon size={64} />}
          title={needle === '' ? 'Todavía no hay comidas registradas' : 'Nada coincide con eso'}
        >
          <p>
            {needle === ''
              ? 'Los últimos siete días van a aparecer acá.'
              : 'Probá con otra palabra: la búsqueda mira la descripción de los ítems.'}
          </p>
        </EmptyState>
      )}

      {visible.map((day) => (
        <section key={day.date} className="stack">
          <div className="row-between">
            <Eyebrow>{describeDay(day.date)}</Eyebrow>
            <span className="micro muted numeric">{formatKcal(dayTotal(day.meals))} kcal</span>
          </div>

          <Card size="flush">
            {day.meals.map((meal) => (
              <div key={meal.id} className="item-row" style={{ cursor: 'default' }}>
                <ItemThumb
                  mealId={meal.id}
                  itemId={meal.items[0]?.id ?? ''}
                  media={meal.items[0]?.media[0] ?? null}
                  ean={meal.items[0]?.ean ?? null}
                  alt={mealTypeLabel(meal.type)}
                />

                <button
                  type="button"
                  className="item-body"
                  onClick={() => navigate(`/comidas/${meal.id}`)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'left',
                    padding: 0,
                  }}
                >
                  <span className="item-name">
                    {meal.items
                      .map((item) => item.freeText)
                      .filter(Boolean)
                      .join(', ') || mealTypeLabel(meal.type)}
                  </span>
                  <span className="item-meta">
                    {formatTime(meal.occurredAt)} · {meal.items.length}{' '}
                    {meal.items.length === 1 ? 'ítem' : 'ítems'}
                  </span>
                </button>

                <Button
                  variant="quiet"
                  style={{ minHeight: 36, paddingInline: 'var(--space-3)' }}
                  disabled={duplicating === meal.id}
                  onClick={() => void duplicate(meal.id)}
                >
                  {duplicating === meal.id ? 'Copiando…' : 'Duplicar'}
                </Button>
              </div>
            ))}
          </Card>
        </section>
      ))}
    </>
  );
}

function dayTotal(meals: Meal[]): number {
  return meals.reduce(
    (total, meal) =>
      total +
      meal.items.reduce(
        (mealTotal, item) =>
          mealTotal + (item.countsTowardsTotals ? (item.currentEstimate?.kcal ?? 0) : 0),
        0,
      ),
    0,
  );
}
